// src/components/RoomSelectionStep.js
import React from 'react';
import { format, differenceInDays } from 'date-fns';
import { Minus, Plus, Users, ChevronLeft, ChevronRight, Image, X } from 'lucide-react';

const amenityConfig = {
  wifi: { icon: require('lucide-react').Wifi, fr: 'Wifi', en: 'Wifi' },
  tv: { icon: require('lucide-react').Tv, fr: 'TV', en: 'TV' },
  airConditioner: { icon: require('lucide-react').Snowflake, fr: 'Climatisation', en: 'Air Conditioning' },
  fan: { icon: require('lucide-react').Wind, fr: 'Ventilateur', en: 'Fan' },
  heater: { icon: require('lucide-react').ThermometerSun, fr: 'Chauffage', en: 'Heating' },
  refrigerator: { icon: require('lucide-react').Home, fr: 'Réfrigérateur', en: 'Refrigerator' },
  microwave: { icon: require('lucide-react').Home, fr: 'Micro-ondes', en: 'Microwave' },
  coffee: { icon: require('lucide-react').Coffee, fr: 'Cafetière', en: 'Coffee Machine' },
  shower: { icon: require('lucide-react').ShowerHead, fr: 'Douche', en: 'Shower' },
  bathtub: { icon: require('lucide-react').Bath, fr: 'Baignoire', en: 'Bathtub' },
  parking: { icon: require('lucide-react').Car, fr: 'Parking', en: 'Parking' },
  breakfast: { icon: require('lucide-react').Utensils, fr: 'Petit-déjeuner', en: 'Breakfast' },
  restaurant: { icon: require('lucide-react').ChefHat, fr: 'Restaurant', en: 'Restaurant' },
  gym: { icon: require('lucide-react').Dumbbell, fr: 'Gym', en: 'Gym' },
  indoorPool: { icon: require('lucide-react').Waves, fr: 'Piscine intérieure', en: 'Indoor Pool' },
  outdoorPool: { icon: require('lucide-react').Waves, fr: 'Piscine extérieure', en: 'Outdoor Pool' },
  sauna: { icon: require('lucide-react').ThermometerSun, fr: 'Sauna', en: 'Sauna' },
  hammam: { icon: require('lucide-react').Droplets, fr: 'Hammam', en: 'Hammam' },
  nordicBath: { icon: require('lucide-react').Archive, fr: 'Bain Nordique', en: 'Nordic Bath' },
  cinema: { icon: require('lucide-react').Film, fr: 'Cinéma', en: 'Cinema' },
  karaoke: { icon: require('lucide-react').Mic, fr: 'Karaoké', en: 'Karaoke' },
  boardGames: { icon: require('lucide-react').Dices, fr: 'Jeux de Société', en: 'Board Games' },
  electricCharging: { icon: require('lucide-react').BatteryMedium, fr: 'Recharge Véhicule', en: 'EV Charging' },
};

const RoomSelectionStep = ({
  // Props principales
  adminMode,
  language,
  t,
  dateLocale,
  
  // États
  roomTypes,
  rooms,
  reservations,
  temporaryBookings,
  roomBlocks,
  selectedCheckIn,
  selectedCheckOut,
  selectedRoomType,
  setSelectedRoomType,
  selectedRoomCount,
  setSelectedRoomCount,
  selectedOccupancy,
  setSelectedOccupancy,
  selectedRoomOccupancies,
  setSelectedRoomOccupancies,
  selectedRoomIds,
  setSelectedRoomIds,
  pendingRoomSelections,
  setPendingRoomSelections,
  selectedImageIndex,
  setSelectedImageIndex,
  displayPrices,
  defaultRules,
  sessionId,
  
  // Fonctions
  getMaxAvailableRoomsForPeriod,
  getMaxAvailableOccupancyWrapper,
  isRoomTypeAvailableForDatesWrapper,
  getRoomPriceForMultipleRooms,
  longStayDiscounts,
  getTotalGuests,
  getTotalRooms,
  getTotalGuestsForType,
  pluralize,
  setStep,
  handleContinueToOptions,
  transitioning,
  isSplitStaySegment,
  splitStaySegmentLabel,
  createTemporaryBooking,
  setTemporaryBookingIds
}) => {

      // ✅ AJOUTER CES LIGNES AU DÉBUT
  const slideStyle = `
    @keyframes slide {
      0%, 100% { transform: translateX(0); }
      50% { transform: translateX(8px); }
    }
  `;

  const handlePreviousImage = (roomTypeId, e) => {
    e.stopPropagation();
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    const currentIndex = selectedImageIndex[roomTypeId] || 0;
    const photos = roomType.photos || [];
    
    if (photos.length > 0) {
      const newIndex = currentIndex === 0 ? photos.length - 1 : currentIndex - 1;
      setSelectedImageIndex(prev => ({ ...prev, [roomTypeId]: newIndex }));
    }
  };

  const handleNextImage = (roomTypeId, e) => {
    e.stopPropagation();
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    const currentIndex = selectedImageIndex[roomTypeId] || 0;
    const photos = roomType.photos || [];
    
    if (photos.length > 0) {
      const newIndex = (currentIndex + 1) % photos.length;
      setSelectedImageIndex(prev => ({ ...prev, [roomTypeId]: newIndex }));
    }
  };

  // MODE ADMIN : Sélection directe des chambres
  if (adminMode) {

        console.log('🔍 Mode Admin - Chambres reçues:', rooms.length);
    console.log('🔍 Chambres internes:', rooms.filter(r => r.isInternal).length);

    const availableRooms = rooms.filter(room => {
      const hasReservation = reservations.some(res => {
        if (res.status === 'cancelled') return false;
        
        let isRoomInReservation = false;
        if (res.rooms && Array.isArray(res.rooms)) {
          isRoomInReservation = res.rooms.some(r => r.roomId === room.id);
        } else if (res.roomId === room.id) {
          isRoomInReservation = true;
        }
        
        if (!isRoomInReservation) return false;
        
        const resCheckIn = res._checkInNorm || (res.checkIn?.toDate ? res.checkIn.toDate() : new Date(res.checkIn));
        const resCheckOut = res._checkOutNorm || (res.checkOut?.toDate ? res.checkOut.toDate() : new Date(res.checkOut));
        return (selectedCheckIn < resCheckOut && selectedCheckOut > resCheckIn);
      });
      
      const hasTemp = temporaryBookings.some(temp => 
        temp.roomId === room.id && 
        temp.sessionId !== sessionId &&
        selectedCheckIn < temp.checkOut && 
        selectedCheckOut > temp.checkIn
      );
      
      const hasBlock = roomBlocks.some(block => {
        if (block.roomId !== room.id) return false;
        const blockStart = new Date(block.startDate);
        const blockEnd = new Date(block.endDate);
        blockStart.setHours(0,0,0,0);
        blockEnd.setHours(0,0,0,0);
        return (selectedCheckIn < blockEnd && selectedCheckOut > blockStart);
      });
      
      return !hasReservation && !hasTemp && !hasBlock;
    });
    
    if (availableRooms.length === 0) {
      return (
        <div className="border border-black rounded-lg p-8 text-center">
          <h3 className="text-xl font-semibold text-black mb-2">Aucune chambre disponible</h3>
          <p className="text-gray-600 mb-6">
            Toutes les chambres sont réservées pour cette période.
          </p>
          <button 
            onClick={() => setStep(1)}
            className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800"
          >
            Choisir d'autres dates
          </button>
        </div>
      );
    }
    
    // ✅ NOUVEAU : Créer une liste de toutes les configurations possibles
    const roomConfigurations = [];
    availableRooms.forEach(room => {
      // Trouver TOUS les types auxquels cette chambre appartient
      const possibleRoomTypes = roomTypes.filter(rt => 
        rt.assignedRooms && rt.assignedRooms.includes(room.id)
      );
      
      // Pour chaque type, créer une configuration
      possibleRoomTypes.forEach(roomType => {
        const configKey = `${room.id}_${roomType.id}`;
        const maxOcc = roomType.maxOccupancy || roomType.maxGuests || 2;
        const price = displayPrices[roomType.id] || roomType.basePrice || 0;
        
        roomConfigurations.push({
          configKey,
          roomId: room.id,
          roomNumber: room.roomNumber || room.id.slice(0,8),
          roomType,
          roomTypeId: roomType.id,
          maxOccupancy: maxOcc,
          price
        });
      });
    });
    
    // Trier par numéro de chambre puis par capacité
    roomConfigurations.sort((a, b) => {
      const numA = a.roomNumber.localeCompare(b.roomNumber);
      if (numA !== 0) return numA;
      return a.maxOccupancy - b.maxOccupancy;
    });
    
    return (
      <>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Mode Administrateur :</strong> Sélectionnez directement les chambres dans la configuration souhaitée. Chaque chambre peut avoir plusieurs configurations (2 ou 3 personnes) avec des prix différents.
          </p>
        </div>
        
        <div className="space-y-4">
          {roomConfigurations.map(config => {
            const isSelected = selectedRoomIds.includes(config.roomId);
            const isThisConfigSelected = isSelected && selectedOccupancy[config.roomId + '_type'] === config.roomTypeId;
            const isOtherConfigSelected = isSelected && !isThisConfigSelected;
            const occupancy = isThisConfigSelected ? (selectedOccupancy[config.roomId] || config.maxOccupancy) : config.maxOccupancy;
            
            return (
              <div 
                key={config.configKey}
                className={`border rounded-lg p-4 transition-all ${
                  isThisConfigSelected 
                    ? 'border-black bg-gray-50 cursor-pointer' 
                    : isOtherConfigSelected
                      ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                      : 'border-gray-200 hover:border-gray-400 cursor-pointer'
                }`}
                onClick={() => {
                  if (isOtherConfigSelected) return; // Empêcher de sélectionner un autre format de la même chambre
                  
                  if (isThisConfigSelected) {
                    // Désélectionner
                    setSelectedRoomIds(prev => prev.filter(id => id !== config.roomId));
                    const newOcc = {...selectedOccupancy};
                    delete newOcc[config.roomId];
                    delete newOcc[config.roomId + '_type'];
                    setSelectedOccupancy(newOcc);
                  } else {
                    // Sélectionner
                    setSelectedRoomIds(prev => [...prev, config.roomId]);
                    setSelectedOccupancy(prev => ({
                      ...prev,
                      [config.roomId]: config.maxOccupancy,
                      [config.roomId + '_type']: config.roomTypeId // Stocker le type choisi
                    }));
                  }
                }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">
                      Chambre {config.roomNumber}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {config.roomType.nameFr || config.roomType.name}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Capacité : {config.maxOccupancy} pers.
                    </p>
                    {isOtherConfigSelected && (
                      <p className="text-xs text-orange-600 mt-1">
                        ⚠️ Cette chambre est déjà sélectionnée dans une autre configuration
                      </p>
                    )}
                  </div>
                  
                  <div className="text-right">
                    <p className="text-2xl font-bold">
                      {config.price}€
                    </p>
                    <p className="text-sm text-gray-600">{t.perNight}</p>
                  </div>
                </div>
                
                {isThisConfigSelected && (
                  <div className="mt-4 pt-4 border-t">
                    <label className="block text-sm font-medium mb-2">Nombre de personnes</label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (occupancy > 1) {
                            setSelectedOccupancy(prev => ({
                              ...prev,
                              [config.roomId]: occupancy - 1
                            }));
                          }
                        }}
                        disabled={occupancy <= 1}
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50"
                      >
                        <Minus size={16} />
                      </button>
                      <span className="w-12 text-center font-semibold">{occupancy}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (occupancy < config.maxOccupancy) {
                            setSelectedOccupancy(prev => ({
                              ...prev,
                              [config.roomId]: occupancy + 1
                            }));
                          }
                        }}
                        disabled={occupancy >= config.maxOccupancy}
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50"
                      >
                        <Plus size={16} />
                      </button>
                      <span className="text-xs text-gray-500 ml-2">(max {config.maxOccupancy})</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {selectedRoomIds.length > 0 && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm font-semibold mb-2">Résumé de la sélection :</p>
            {selectedRoomIds.map(roomId => {
              const room = rooms.find(r => r.id === roomId);
              const roomTypeId = selectedOccupancy[roomId + '_type'];
              const roomType = roomTypes.find(rt => rt.id === roomTypeId);
              const occupancy = selectedOccupancy[roomId] || 2;
              
              return (
                <div key={roomId} className="text-sm mb-1">
                  • Chambre {room?.roomNumber} - {roomType?.nameFr || roomType?.name} ({occupancy} pers.)
                </div>
              );
            })}
          </div>
        )}
        
        <div className="mt-8 flex justify-between">
          <button 
            onClick={() => {
              setStep(1);
              setSelectedOccupancy({});
              setSelectedRoomIds([]);
            }}
            className="px-6 py-3 border border-gray-300 hover:bg-gray-50 rounded-lg"
          >
            Retour aux dates
          </button>
          
          <button 
            onClick={async () => {
              if (selectedRoomIds.length === 0) return;
              
              try {
                const newTempIds = [];
                for (const roomId of selectedRoomIds) {
                  const tempId = await createTemporaryBooking(
                    roomId,
                    selectedCheckIn,
                    selectedCheckOut,
                    sessionId
                  );
                  newTempIds.push(tempId);
                  console.log(`✅ Chambre ${roomId} bloquée temporairement`);
                }
                
                setTemporaryBookingIds(newTempIds);
                
                const firstRoom = rooms.find(r => r.id === selectedRoomIds[0]);
                const firstRoomTypeId = selectedOccupancy[firstRoom.id + '_type'];
                if (firstRoomTypeId) {
                  setSelectedRoomType(firstRoomTypeId);
                }
                
                setStep(3);
              } catch (error) {
                console.error('Erreur création temporary booking:', error);
                alert('Erreur lors du blocage des chambres');
              }
            }}
            disabled={selectedRoomIds.length === 0}
            className="px-6 py-3 bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
          >
            Continuer vers les options
          </button>
        </div>
      </>
    );
  }

  // MODE PUBLIC : Sélection par type de chambre (CODE EXISTANT 100% CONSERVÉ)
  // Pour les segments de split stay, on bypass les règles de pattern (vérification dispo physique uniquement)
  const availableRoomTypes = roomTypes.filter(rt => {
    if ((selectedRoomCount[rt.id] || 0) > 0) return true; // toujours garder les types déjà sélectionnés
    if (!isSplitStaySegment && !isRoomTypeAvailableForDatesWrapper(rt.id, selectedCheckIn, selectedCheckOut)) return false;
    const maxRooms = getMaxAvailableRoomsForPeriod(rt.id, selectedCheckIn, selectedCheckOut);
    return maxRooms > 0 || getTotalRooms() >= (defaultRules.maxRoomsPerBooking || 5);
  });
  
  if (availableRoomTypes.length === 0) {
    return (
      <div className="border border-black rounded-lg p-8 text-center">
        <div className="mb-4">
          <svg className="w-16 h-16 text-black mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-black mb-2">
          {t.noRoomsAvailable}
        </h3>
        <p className="text-black mb-2">
          {t.allRoomsBooked}
        </p>
        <p className="text-gray-600 mb-6">
          {language === 'fr' ? 'Du' : 'From'} <strong>{format(selectedCheckIn, language === 'fr' ? 'dd MMMM yyyy' : 'MMMM dd, yyyy', { locale: dateLocale })}</strong> {language === 'fr' ? 'au' : 'to'}{' '}
          <strong>{format(selectedCheckOut, language === 'fr' ? 'dd MMMM yyyy' : 'MMMM dd, yyyy', { locale: dateLocale })}</strong>
        </p>
        <div className="flex gap-3 justify-center">
          <button 
            onClick={() => {
              setStep(1);
              setSelectedOccupancy({});
              setSelectedRoomCount({});
              setSelectedRoomType(null);
            }}
            className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800"
          >
            {t.chooseOtherDates}
          </button>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t.refreshPage}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <div className="space-y-6">
        {availableRoomTypes.map(roomType => {
          const maxRooms = getMaxAvailableRoomsForPeriod(roomType.id, selectedCheckIn, selectedCheckOut);
          const maxOccupancy = getMaxAvailableOccupancyWrapper(roomType.id, selectedCheckIn, selectedCheckOut);
const selected = (selectedRoomCount[roomType.id] || 0) > 0;
const isOpen = selectedRoomType === roomType.id || selected;
          const roomCount = selectedRoomCount[roomType.id] !== undefined ? selectedRoomCount[roomType.id] : 0;
          const occupancy = selectedOccupancy[roomType.id] || roomType.defaultOccupancy || 2;
          
          const roomName = language === 'fr' ? 
            (roomType.nameFr || roomType.name) : 
            (roomType.nameEn || roomType.name);
          const roomDescription = language === 'fr' ? 
            (roomType.descriptionFr || roomType.description || '') : 
            (roomType.descriptionEn || roomType.description || '');

          return (
            <div 
              key={roomType.id}
className={`relative border rounded-lg overflow-hidden transition-all ${
  selected ? 'border-yellow-400 bg-amber-50 cursor-default' : 'border-gray-200 hover:border-gray-400 cursor-pointer'
}`}
              onClick={() => {
                if (selected) return;
                setSelectedRoomType(roomType.id);
                if (!selectedOccupancy[roomType.id]) {
                  setSelectedOccupancy(prev => ({...prev, [roomType.id]: roomType.defaultOccupancy || 2}));
                }
              }}
            >
              {selected && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRoomCount(prev => ({...prev, [roomType.id]: 0}));
                    setSelectedRoomOccupancies(prev => { const n = {...prev}; delete n[roomType.id]; return n; });
                    setPendingRoomSelections(prev => { const n = {...prev}; delete n[roomType.id]; return n; });
                    if (selectedRoomType === roomType.id) setSelectedRoomType(null);
                  }}
                  className="absolute top-2 right-2 z-10 flex flex-row items-center gap-1.5"
                  title={language === 'fr' ? 'Annuler la sélection' : 'Cancel selection'}
                >
                  <span className="text-[11px] text-yellow-700 font-medium">{language === 'fr' ? 'Annuler' : 'Cancel'}</span>
                  <span className="w-7 h-7 rounded-full bg-yellow-300 hover:bg-yellow-400 flex items-center justify-center">
                    <X className="w-4 h-4 text-yellow-900" />
                  </span>
                </button>
              )}
              <div className="block lg:flex">
                <div className="w-full lg:w-1/3 lg:min-w-[300px] relative">
                  {roomType.photos && roomType.photos.length > 0 ? (
                    <div className="relative h-56 lg:h-48 bg-gray-100">
                      <img 
                        src={roomType.photos[selectedImageIndex[roomType.id] || 0]} 
                        alt={roomName}
                        className="w-full h-full object-cover"
                      />
                      
                      {roomType.photos.length > 1 && (
                        <>
                          <button
                            onClick={(e) => handlePreviousImage(roomType.id, e)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full p-1.5 hover:bg-opacity-70 transition-opacity"
                            aria-label="Image précédente"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                          
                          <button
                            onClick={(e) => handleNextImage(roomType.id, e)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full p-1.5 hover:bg-opacity-70 transition-opacity"
                            aria-label="Image suivante"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                          
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 bg-black bg-opacity-50 px-2 py-1 rounded-full">
                            {roomType.photos.map((_, index) => (
                              <button
                                key={index}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedImageIndex(prev => ({ ...prev, [roomType.id]: index }));
                                }}
                                className={`w-1.5 h-1.5 rounded-full transition-all ${
                                  index === (selectedImageIndex[roomType.id] || 0)
                                    ? 'bg-white w-3'
                                    : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                                }`}
                                aria-label={`Image ${index + 1}`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="h-56 lg:h-48 bg-gray-100 flex items-center justify-center">
                      <div className="text-center text-gray-400">
                        <Image className="w-16 h-16 mx-auto mb-2" />
                        <p className="text-sm">Pas de photo</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className={`flex-1 p-4 lg:p-6 ${selected ? 'pt-12 lg:pt-12' : ''}`}>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold">{roomName}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        <Users className="inline w-4 h-4 mr-1" />
                        {language === 'fr' ? 'Capacité :' : 'Capacity:'} {maxOccupancy} {maxOccupancy > 1 ? t.persons : t.person}
                      </p>
                    </div>

                    <div className="text-left sm:text-right">
                      {(() => {
                        const basePrice = displayPrices[roomType.id] || roomType.basePrice;
                        const d = longStayDiscounts?.[roomType.id];
                        let discountedPrice = null;
                        if (d) {
                          if (d.type === 'adjustedNightly') discountedPrice = Math.round(basePrice - d.reduction);
                          else if (d.type === 'adjustedPercentage') discountedPrice = Math.round(basePrice * (1 - d.reduction / 100));
                          else if (d.type === 'weekly') discountedPrice = Math.round(basePrice - d.reduction / 7);
                          else if (d.type === 'monthly') discountedPrice = Math.round(basePrice - d.reduction / 30);
                        }
                        return discountedPrice !== null ? (
                          <>
                            <p className="text-sm text-gray-400 line-through">{basePrice}€</p>
                            <p className="text-2xl font-bold text-green-700">{discountedPrice}€</p>
                            <p className="text-sm text-gray-600">{t.perNight}</p>
                            <p className="mt-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded px-2 py-0.5 inline-block">
                              {typeof d.name === 'object' ? (d.name[language] || d.name.fr || '') : (d.name || '')}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-2xl font-bold">{basePrice}€</p>
                            <p className="text-sm text-gray-600">{t.perNight}</p>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t pt-4 space-y-4">
                      {roomDescription && (
                        <div className="text-sm text-gray-600 p-3 bg-gray-50 rounded whitespace-pre-wrap">
                          {roomDescription}
                        </div>
                      )}

                      {roomType.amenities && Object.keys(roomType.amenities).filter(key => roomType.amenities[key]).length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(roomType.amenities).map(([key, value]) => {
                            if (!value || !amenityConfig[key]) return null;
                            const AmenityIcon = amenityConfig[key].icon;
                            const label = language === 'fr' ? amenityConfig[key].fr : amenityConfig[key].en;
                            return (
                              <span key={key} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-900 text-white rounded text-xs">
                                <AmenityIcon className="w-3 h-3" />
                                <span>{label}</span>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      


<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
  <div className="flex items-center gap-2">
    <span className={`text-sm font-medium transition-colors ${
      roomCount === 0 ? 'text-red-600 animate-pulse' : 'text-black'
    }`}>
      {t.numberOfRooms}
    </span>
    {roomCount === 0 && (
      <>
        <style>{slideStyle}</style>
        <span className="text-red-600 text-xl" style={{ animation: 'slide 1s ease-in-out infinite' }}>→</span>
      </>
    )}
  </div>
  <div className="flex items-center gap-2">
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (roomCount > 0) {
          const newCount = roomCount - 1;
          setSelectedRoomCount(prev => ({
            ...prev,
            [roomType.id]: newCount
          }));
          
          if (newCount === 0) {
            setSelectedRoomOccupancies(prev => {
              const newOcc = {...prev};
              delete newOcc[roomType.id];
              return newOcc;
            });
            setPendingRoomSelections(prev => {
              const newSel = {...prev};
              delete newSel[roomType.id];
              return newSel;
            });
          } else {
            setSelectedRoomOccupancies(prev => ({
              ...prev,
              [roomType.id]: (prev[roomType.id] || []).slice(0, newCount)
            }));
            const rt = roomTypes.find(r => r.id === roomType.id);
            if (rt?.assignedRooms) {
              const availableRoomsForType = rooms.filter(r => 
                rt.assignedRooms.includes(r.id)
              ).slice(0, newCount).map(r => r.id);
              
              setPendingRoomSelections(prev => ({
                ...prev,
                [roomType.id]: {
                  count: newCount,
                  assignedRooms: availableRoomsForType
                }
              }));
            }
          }
        }
      }}
      disabled={roomCount <= 0}
      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50"
    >
      <Minus size={16} />
    </button>
    <span className="w-12 text-center font-semibold">{roomCount}</span>
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (roomCount < maxRooms) {
          const newCount = roomCount + 1;
          setSelectedRoomCount(prev => ({
            ...prev,
            [roomType.id]: newCount
          }));
          const maxCapacity = roomType.maxOccupancy || roomType.maxGuests || 2;
          setSelectedRoomOccupancies(prev => ({
            ...prev,
            [roomType.id]: [...(prev[roomType.id] || []), maxCapacity]
          }));
          const rt = roomTypes.find(r => r.id === roomType.id);
          if (rt?.assignedRooms) {
            const availableRoomsForType = rooms.filter(r => 
              rt.assignedRooms.includes(r.id)
            ).slice(0, newCount).map(r => r.id);
            
            setPendingRoomSelections(prev => ({
              ...prev,
              [roomType.id]: {
                count: newCount,
                assignedRooms: availableRoomsForType
              }
            }));
          }
        }
      }}
      disabled={roomCount >= maxRooms || (!adminMode && getTotalRooms() >= (defaultRules.maxRoomsPerBooking || 5))}
      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50"
    >
      <Plus size={16} />
    </button>
  </div>
</div>
{!adminMode && getTotalRooms() >= (defaultRules.maxRoomsPerBooking || 5) && (
  <p className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded p-3 mt-2">
    {(t.maxRoomsReached || 'Maximum {max} chambre{s} atteint — désélectionnez un type pour en choisir un autre.')
      .replace('{max}', defaultRules.maxRoomsPerBooking || 5)
      .replace(/{s}/g, (defaultRules.maxRoomsPerBooking || 5) > 1 ? 's' : '')}
  </p>
)}

                      {roomCount > 0 && (
                        <div className="space-y-3">
                          <span className="text-sm font-medium block">{t.occupancyPerRoom}</span>
                          {Array.from({ length: roomCount }, (_, index) => {
                            const occupancies = selectedRoomOccupancies[roomType.id] || [];
                            const occupancy = occupancies[index] || 2;
                            const maxOccupancy = roomType.maxOccupancy || roomType.maxGuests || 2;
                            
                            return (
                              <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                <span className="text-sm">{t.room} {index + 1}</span>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (occupancy > 1) {
                                        const newOccupancies = [...(occupancies.length ? occupancies : Array(roomCount).fill(2))];
                                        newOccupancies[index] = occupancy - 1;
                                        setSelectedRoomOccupancies(prev => ({
                                          ...prev,
                                          [roomType.id]: newOccupancies
                                        }));
                                      }
                                    }}
                                    disabled={occupancy <= 1}
                                    className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50"
                                  >
                                    <Minus size={14} />
                                  </button>
                                  <span className="w-8 text-center text-sm font-medium">{occupancy}</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (occupancy < maxOccupancy) {
                                        const newOccupancies = [...(occupancies.length ? occupancies : Array(roomCount).fill(2))];
                                        newOccupancies[index] = occupancy + 1;
                                        setSelectedRoomOccupancies(prev => ({
                                          ...prev,
                                          [roomType.id]: newOccupancies
                                        }));
                                      }
                                    }}
                                    disabled={occupancy >= maxOccupancy}
                                    className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50"
                                  >
                                    <Plus size={14} />
                                  </button>
                                  <span className="text-xs text-gray-500">{t.pers}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      <div className="bg-gray-100 p-3 rounded-lg">
                        <div className="flex flex-col sm:flex-row sm:justify-between text-sm gap-1">
                          <span>{roomCount} {pluralize(t.roomsFor, roomCount).replace(/{s}/g, roomCount > 1 ? 's' : '')} × {differenceInDays(selectedCheckOut, selectedCheckIn)} {differenceInDays(selectedCheckOut, selectedCheckIn) > 1 ? t.nights : t.night}</span>
                          <strong>{getRoomPriceForMultipleRooms(roomType, roomCount)}€</strong>
                        </div>
                        <div className="text-sm mt-1 text-gray-600">
                          <span>{t.total}: {getTotalGuestsForType(roomType.id)} {getTotalGuestsForType(roomType.id) > 1 ? t.persons : t.person}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>





{getTotalRooms() > 0 && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold mb-3 text-sm">{t.summary}</h3>
          <ul className="space-y-2 text-sm">
            {Object.entries(selectedRoomCount)
              .filter(([_, count]) => count > 0)
              .map(([typeId, count]) => {
                const rt = roomTypes.find(t => t.id === typeId);
                const roomName = language === 'fr' ? (rt?.nameFr || rt?.name) : (rt?.nameEn || rt?.name);
                const occupancies = selectedRoomOccupancies[typeId] || [];
                const totalGuests = occupancies.reduce((sum, occ) => sum + occ, 0);
                
                return (
                  <li key={typeId} className="flex items-start gap-2">
                    <span>•</span>
                    <span>
                      {count} {roomName} {language === 'fr' ? 'pour' : 'for'} {totalGuests} {totalGuests > 1 ? t.persons : t.person}
                    </span>
                  </li>
                );
              })}
          </ul>
          <p className="text-sm mt-3 pt-3 border-t border-blue-200">
            <strong>Total :</strong> {getTotalRooms()} {pluralize(t.roomsFor, getTotalRooms()).replace(/{s}/g, getTotalRooms() > 1 ? 's' : '')} {getTotalGuests()} {getTotalGuests() > 1 ? t.persons : t.person}
          </p>
          {(() => {
            const totalPrice = Object.entries(selectedRoomCount)
              .filter(([_, c]) => c > 0)
              .reduce((sum, [typeId, count]) => {
                const rt = roomTypes.find(r => r.id === typeId);
                return rt ? sum + getRoomPriceForMultipleRooms(rt, count) : sum;
              }, 0);
            return totalPrice > 0 ? (
              <p className="text-base font-bold mt-2 text-blue-900">
                {language === 'fr' ? 'Total séjour :' : 'Total stay:'} {totalPrice}€
              </p>
            ) : null;
          })()}



          {!adminMode && getTotalRooms() >= (defaultRules.maxRoomsPerBooking || 5) && (
            <p className="text-xs text-gray-600 mt-2">
              {t.maxSimultaneous.replace('{max}', defaultRules.maxRoomsPerBooking || 5).replace(/{s}/g, (defaultRules.maxRoomsPerBooking || 5) > 1 ? 's' : '')}
            </p>
          )}
        </div>
      )}

      <div className="mt-8 pb-28">
        <button
          onClick={() => {
            setStep(1);
            setSelectedOccupancy({});
            setSelectedRoomCount({});
            setSelectedRoomType(null);
          }}
          className="px-6 py-3 border border-gray-300 hover:bg-gray-50 rounded-lg"
        >
          {t.backToDates}
        </button>
      </div>
    </>
  );
};

export default RoomSelectionStep;